// Prepares a photo on the device before upload.
//
// Drawing the image onto a canvas and re-encoding it as JPEG throws away all
// metadata, including the GPS coordinates phones embed in photos. That matters
// here: a photo taken at home would otherwise carry the home's exact location.
// It also shrinks a 4–12 MB phone photo to a few hundred KB. (The server strips
// metadata again, since it can't trust clients.)

const MAX_EDGE = 1600;
const QUALITY = 0.82;

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
  /** Object URL for the composer preview. Revoke when done. */
  previewUrl: string;
}

export class PhotoPrepareError extends Error {}

/** iPhone photos (HEIC/HEIF). Safari reads them; most desktop browsers can't. */
export function isHeic(file: Pick<File, 'name' | 'type'>): boolean {
  return /^image\/hei[cf](-sequence)?$/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

function loadImage(file: File): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  // <img> decoding respects EXIF orientation and handles HEIC on Apple devices.
  return img
    .decode()
    .then(() => img)
    .catch(() => {
      throw new PhotoPrepareError(
        isHeic(file)
          ? "This browser can't open iPhone HEIC photos. Post from your phone, or save the photo as a JPEG first."
          : "That file couldn't be read as a photo. Try a JPEG or PNG.",
      );
    })
    .finally(() => URL.revokeObjectURL(url));
}

export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  if (!file.type.startsWith('image/') && file.type !== '') {
    throw new PhotoPrepareError('Only photos can be attached.');
  }
  const img = await loadImage(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new PhotoPrepareError("This browser can't prepare photos.");
  // Paper-colored backdrop so transparent PNGs don't turn black.
  ctx.fillStyle = '#fbf7ee';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', QUALITY));
  if (!blob) throw new PhotoPrepareError("That photo couldn't be prepared. Try a different one.");
  return { blob, width, height, previewUrl: URL.createObjectURL(blob) };
}
